// Moved from `src/features/dictationLogs/hooks/useDictationLogs.ts` (now a pure
// delegate) at D1/D2, ARCFE-1084. Basename preserved — it is a `jest.mock`
// target for three app suites.
//
// TWO IMPORTS HAD TO BE SHED AND EACH HEAL IS RECORDED AT ITS SITE:
//
//   * `@/lib/SessionRecordingProvider/t11nClient` -> `../api/logs`, over the
//     loader façade. This is the whole D1 t11nClient split.
//   * `@/store` + `react-redux` (severance cohort S2) -> the seam's
//     `useAuthToken`. REACTIVE ON PURPOSE: the pre-move read was
//     `useSelector((state: RootState) => state.auth.token)` and the app injects
//     `useAuthToken` from `@/hooks/useIsAuthenticated`, which is that exact
//     expression — same subscription, same re-render timing. The preferences
//     carve (ARCFE-1067) deliberately took a boolean `enabled` instead, because
//     ITS pre-move read was a non-subscribing `store.getState()`; copying that
//     choice here would have changed both when the query flips enabled AND the
//     `isPending` skeleton window below, which reads the token directly.
//
// `DateRange` from `@heidi/design-system/date-range-picker` also could not
// cross — `/data` is headless. It is replaced by the STRUCTURAL shape the hook
// actually consumes (two optional `Date`s, read only via `.getTime()`), which
// every `DateRange` value is assignable to, so `atoms.ts` and every caller
// typecheck unchanged. No design-system type reaches this package's surface.
import {
  type InfiniteData,
  type QueryObserverResult,
  type RefetchOptions,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { DICTATION_LOGS_INFINITE_KEY, type DictationLogEntry } from '../../contracts';
import { type DictationsResponse, getDictations, searchDictations } from '../api/logs';
import { getDictationDataConfig } from '../config';

const PAGE_LIMIT = 30;
const SEARCH_MIN_LENGTH = 2;

export { DICTATION_LOGS_INFINITE_KEY };

/**
 * Structural stand-in for the design-system `DateRange`.
 *
 * Declared here rather than imported because `/data` is headless (the ARCFE-921
 * severance, asserted by this package's V3 fixture). Both fields are optional
 * `Date`s, which is what the hook reads and what makes every `DateRange` value
 * assignable to it.
 */
export interface DictationLogsDateRange {
  from?: Date | undefined;
  to?: Date | undefined;
}

export interface UseDictationLogsParams {
  search?: string;
  dateRange?: DictationLogsDateRange;
  /**
   * Extra gate for callers that only need the query under narrow conditions
   * (e.g. cross-sell eligibility in GlobalModalsWrapper). Defaults to true.
   */
  enabled?: boolean;
}

export interface UseDictationLogsResult {
  logs: DictationLogEntry[];
  total: number | undefined;
  isPending: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  isPlaceholderData: boolean;
  hasNextPage: boolean;
  error: Error | null;
  fetchNextPage: () => void;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<InfiniteData<DictationsResponse>, Error>>;
}

function msToIsoOrNull(ms: number | null): string | null {
  return ms !== null ? new Date(ms).toISOString() : null;
}

/**
 * Single infinite-query hook that powers both the paginated list and the
 * search view. The `queryFn` branches between `getDictations` (list) and
 * `searchDictations` (search) based on the trimmed search length; everything
 * else — pagination, key shape, fallback semantics — is identical.
 *
 * At exactly 1 char the query is disabled: the previous result keeps showing
 * (via `keepPreviousData`) so the UI doesn't flicker during ordinary typing.
 * The list↔search transition is handled by `keepPreviousData` retaining the
 * previous observer result across the `queryKey` change.
 */
export function useDictationLogs(
  params: UseDictationLogsParams = {}
): UseDictationLogsResult {
  const { useAuthToken } = getDictationDataConfig();
  const token = useAuthToken();
  const { search, dateRange, enabled: enabledParam = true } = params;
  // Use primitive ms so the queryKey is stable across object-identity changes.
  const fromTime = dateRange?.from?.getTime() ?? null;
  const toTime = dateRange?.to?.getTime() ?? null;
  const trimmedSearch = (search ?? '').trim();
  const isSearch = trimmedSearch.length >= SEARCH_MIN_LENGTH;
  const enabled = enabledParam && !!token && (trimmedSearch.length === 0 || isSearch);

  const {
    data,
    isPending,
    isFetching,
    isFetchingNextPage,
    isRefetching,
    isPlaceholderData,
    hasNextPage,
    error,
    fetchNextPage: queryFetchNextPage,
    refetch,
  } = useInfiniteQuery<
    DictationsResponse,
    Error,
    InfiniteData<DictationsResponse>,
    readonly unknown[],
    string | null
  >({
    queryKey: [DICTATION_LOGS_INFINITE_KEY, trimmedSearch, fromTime, toTime],
    // Branch on `trimmedSearch.length` (not the derived `isSearch`) so the
    // exhaustive-deps lint can see every queryFn-referenced value sit in the
    // queryKey via `trimmedSearch`.
    queryFn: ({ pageParam }) =>
      trimmedSearch.length >= SEARCH_MIN_LENGTH
        ? searchDictations({
            q: trimmedSearch,
            cursor: pageParam ?? undefined,
            limit: PAGE_LIMIT,
            from: msToIsoOrNull(fromTime),
            to: msToIsoOrNull(toTime),
          })
        : getDictations({
            cursor: pageParam ?? undefined,
            limit: PAGE_LIMIT,
            from: msToIsoOrNull(fromTime),
            to: msToIsoOrNull(toTime),
          }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });

  const pages = data?.pages;
  const logs = useMemo(() => pages?.flatMap((p) => p.dictations) ?? [], [pages]);
  // BE returns the same `total` on every page; reading page 0 keeps the count
  // stable as later pages stream in.
  const total = pages?.at(0)?.total;

  const fetchNextPage = useCallback(() => {
    void queryFetchNextPage();
  }, [queryFetchNextPage]);

  return {
    logs,
    total,
    // `isPending` covers `status === 'pending'` regardless of `fetchStatus`,
    // so the empty state can't flash during the `'idle'` window between
    // `resetQueries` and the refetch starting. `isLoading` would miss that.
    // `!token` keeps the skeleton up during the pre-auth window — the query is
    // disabled there, so without this the empty `logs` would flash the
    // "No dictations yet" state before auth resolves.
    isPending: !token || (enabled && isPending),
    isFetchingNextPage,
    isRefetching,
    // Gate on `isFetching` so the table only dims while a real refetch is in
    // flight. In the 1-char dead zone the query is disabled but
    // `keepPreviousData` still surfaces the prior rows as placeholder — without
    // this gate the table would stay dimmed indefinitely.
    isPlaceholderData: isPlaceholderData && isFetching,
    hasNextPage: hasNextPage ?? false,
    error,
    fetchNextPage,
    refetch,
  };
}
